import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { BaseAlertModal } from "../../../../../components/modals/alert";
import { Layout } from "../../../../../containers/layout/dashboard";
import { AddCurrencyWrapper } from "./styled";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { BaseButton } from "../../../../../components/button/styled";
import { BaseFieldSet } from "../../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../../components/formfields/label/styled";
import { BaseSelect } from "../../../../../components/formfields/select/styled";
import { BaseOption } from "../../../../../components/formfields/option/styled";
import { BaseInput } from "../../../../../components/formfields/input/styled";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { Context } from "../../../../../context";
import { addCurrencyService } from "../../../../../utils/apis/configuration/currency/addCurrency";
import { retrieveCountriesService } from "../../../../../config/country/countries";
import { useQuery } from "@tanstack/react-query";

export const AddCurrency = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(Context);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [formDetails, setFormDetails] = useState({
        title: "",
        symbol: "",
        country: " ",
    });

    useEffect(() => {
        setActiveNavItem("Configuration");
    }, [setActiveNavItem]);

    const { data: countries } = useQuery({
        queryKey: ['countries', TOKEN],
        queryFn: async () => retrieveCountriesService(TOKEN),
        enabled: !!TOKEN,
    });

    const handleNavigateToPrevious = () => {
        return navigate(-1);
    }

    const handleAlertModalPersist = () => {
        return setOpenAlertModal(true);
    }

    const handleNavigateToCurrency = () => {
        navigate("/configuration/currency");
        return setOpenAlertModal(false);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        try {
            const response = await addCurrencyService(formDetails, TOKEN);
            if (response.status === "success") {
                setIsLoading(false);
                setOpenAlertModal(true);
            } else {
                setIsLoading(false);
                setError('Failed to add currency. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to add currency. ${error.message}`);
            console.error('Failed to add currency:', error);
        }
    }

    return (
        <Layout
            id="add-currency"
        >
            <AddCurrencyWrapper>
                <BaseAlertModal
                    title="Success"
                    message="Currency successfully added"
                    open={openAlertModal}
                    handleClose={handleAlertModalPersist}
                    callToAction="Continue"
                    handleCallToAction={handleNavigateToCurrency}
                />
                <Box
                    component={"div"}
                    className="title-box"
                >
                    <Typography
                        variant="h1"
                        fontFamily={"Inter"}
                        fontWeight={700}
                        fontSize={24}
                        lineHeight={"normal"}
                        color={"#373737"}
                        marginBlockEnd={"calc(var(--basic-margin)/4)"}
                    >
                        Add Currency
                    </Typography>
                    <BaseButton
                        variant="outlined"
                        border="1px solid #D7D7D7"
                        colour="#373737"
                        bgcolor="#FFFFFF"
                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                        fontSize={12}
                        fontWeight={800}
                        startIcon={<ArrowLeftIcon />}
                        onClick={handleNavigateToPrevious}
                    >
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Back
                        </Typography>
                    </BaseButton>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Country
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="country"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.country}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select country
                                    </BaseOption>
                                    {countries?.map((country: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={country.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {country.title}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Currency name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="title"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter currency name"
                                    value={formDetails.title}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Currency symbol
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="symbol"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter currency symbol"
                                    value={formDetails.symbol}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={16}
                        lineHeight={"normal"}
                        color={"#FF0000"}
                        whiteSpace={"normal"}
                        marginBlockStart={"var(--basic-margin)"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        component={"div"}
                        className="button-box"
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            radius="7px"
                            fontSize={16}
                            fontWeight={800}
                            bgcolor="#791377"
                            padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </AddCurrencyWrapper>
        </Layout>
    )
}